<template>
  <div class="acts-wrapper">
    <h2 class="section-title">{{ actInfo.student_name }}</h2>

    <div class="acts-inner-wrapper">
      <!-- Левая часть (Детальная информация) -->
      <div class="acts-detail">
        <h3 class="section-subtitle">
          {{ $t(`acts.actNumber`) }} {{ actInfo.id }}
        </h3>

        <v-card class="info-card">
          <v-simple-table>
            <tbody>
              <tr>
                <td>{{ $t(`acts.actsDetail.course`) }}</td>
                <td>{{ actInfo.groupe_name }}</td>
              </tr>
              <tr>
                <td>{{ $t(`acts.actsDetail.date`) }}</td>
                <td>{{ actInfo.month }}.{{ actInfo.year }}</td>
              </tr>
              <tr>
                <td>{{ $t(`acts.actsDetail.teacher`) }}</td>
                <td>{{ actInfo.teacher_name }}</td>
              </tr>
              <tr>
                <td>{{ $t(`acts.actsDetail.status`) }}</td>
                <td :style="{ color: actInfo.status_color }">{{ actInfo.status_name }}</td>
              </tr>
              <tr>
                <td>{{ $t(`acts.actsDetail.hoursElapsed`) }}</td>
                <td>{{ actInfo.count }}</td>
              </tr>
            </tbody>
          </v-simple-table>
        </v-card>

        <div class="button-group" v-if="showActionButtons">
          <v-btn class="btn accept-btn" @click="submitAct">
            <span class="btn-text">{{
              $t(`acts.actsDetail.acceptButton`)
            }}</span>
            <span class="btn-icon">
              <img src="@/assets/accept-right.svg" alt="accept" />
            </span>
          </v-btn>

          <v-btn class="btn reject-btn" @click="rejectAct">{{
            $t(`acts.actsDetail.cancelButton`)
          }}</v-btn>
        </div>
      </div>

      <!-- Правая часть (Список актов) -->
      <div class="acts-list">
        <h3 class="section-subtitle">{{ $t(`acts.actsList`) }}</h3>
        <v-card class="acts-list-card">
          <v-list class="acts-v-list" dense>
            <v-list-item-group>
              <v-list-item
                v-for="act in visibleActs"
                :key="act.id"
                @click="selectAct(act)"
                class="acts-item"
              >
                <v-list-item-content>
                  <v-list-item-title>
                    {{ $t(`acts.actNumber`) }} {{ act.actNumber }}
                  </v-list-item-title>
                </v-list-item-content>
                <v-list-item-action>
                  <v-chip
                    class="acts-v-chip"
                    small
                    :color="act.status_color"
                    outlined
                  >
                    {{ act.status }}
                  </v-chip>
                </v-list-item-action>
              </v-list-item>
            </v-list-item-group>
          </v-list>
        </v-card>

        <div class="view-all" v-if="acts.length > 5 && !showAllActs">
          <a href="#" class="view-all-link" @click.prevent="showAllActs = true">
            {{ $t(`acts.showAll`) }}
          </a>
        </div>
      </div>
    </div>

    <!-- Табель успеваемости -->
    <div class="diary-section" v-if="actInfo.diary && actInfo.diary.length > 0">
      <v-card class="diary-card">
        <div class="diary-grid">
          <div class="diary-header">
            <div class="diary-cell">{{ $t('acts.diaryDate') }}</div>
            <div class="diary-cell">{{ $t('acts.diaryTime') }}</div>
            <div class="diary-cell">{{ $t('acts.diaryStatus') }}</div>
            <div class="diary-cell">{{ $t('acts.diaryMark') }}</div>
          </div>
          
          <div 
            v-for="(day, index) in actInfo.diary" 
            :key="day.id"
            class="diary-row"
            :class="{ 'even-row': index % 2 === 0 }"
          >
            <div class="diary-cell">{{ formatDate(day.date) }}</div>
            <div class="diary-cell">{{ day.time_start }} - {{ day.time_end }}</div>
            <div class="diary-cell">
              <v-chip
                small
                :color="day.status_color"
                outlined
              >
                {{ day.status_name }}
              </v-chip>
            </div>
            <div class="diary-cell">
              <v-chip
                v-if="actInfo.marks && actInfo.marks[index]"
                small
                :color="actInfo.marks[index].mark_status_color"
                outlined
              >
                {{ actInfo.marks[index].mark_name }}
              </v-chip>
              <span v-else>-</span>
            </div>
          </div>
        </div>
      </v-card>
    </div>

    <ProgressCircular
      :isSubmitLoading="isSubmitLoading"
      v-if="isLoading || isSubmitLoading"
    />

    <v-dialog v-model="confirmDialog.visible" max-width="500" persistent>
      <v-card>
        <v-card-title class="headline">
          {{ $t("acts.confirmTitle") }}
        </v-card-title>

        <v-card-text>
          <p>
            {{
              confirmDialog.action === "submit"
                ? $t("acts.confirmAcceptText")
                : $t("acts.confirmRejectText")
            }}
          </p>

          <!-- Обязательное поле комментария при отказе -->
          <v-textarea
            v-if="confirmDialog.action === 'reject'"
            v-model="confirmDialog.comment"
            :label="$t('acts.commentPlaceholder')"
            rows="3"
            outlined
            class="mt-3"
          />
        </v-card-text>

        <v-card-actions class="justify-end">
          <v-btn text @click="confirmDialog.visible = false">
            {{ $t("acts.cancel") }}
          </v-btn>
          <v-btn color="primary" @click="handleDialogConfirm">
            {{ $t("acts.confirm") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import ProgressCircular from "@/components/custom/ProgressCircular.vue";
import { showToast } from "@/utils/toast";
import { fetchRejectAct, fetchSubmitAct } from "@/api/profile";

export default {
  components: { ProgressCircular },
  data() {
    return {
      showAllActs: false,
      isLoading: false,
      isSubmitLoading: false,
      confirmDialog: {
        show: false,
        action: null,
        comment: "",
      },
      actStatus: {
        FORMED: 1,
        SUBMITTED: 2,
        REJECTED: 3,
      },
    };
  },
  created() {
    this.initializePageData();
  },
  mounted() {
    console.log("DWD", this.actInfo);
  },
  computed: {
    ...mapGetters("acts", ["acts"]),
    ...mapGetters("acts", ["actInfo"]),
    visibleActs() {
      return this.showAllActs ? this.acts : this.acts.slice(0, 5);
    },
    showActionButtons() {
      return Number(this.actInfo?.status_id) === this.actStatus.FORMED;
    },
  },
  watch: {
    "$route.params.actId": {
      immediate: true,
      handler(newActId) {
        console.log("$route.params.actId");
        this.initializePageData(newActId);
      },
    },
  },
  methods: {
    ...mapActions({
      fetchActInfo: "acts/loadActInfo",
      fetchActs: "acts/loadActs",
    }),
    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleDateString('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    },
    submitAct() {
      this.confirmDialog = {
        visible: true,
        action: "submit",
        comment: "",
      };
    },
    rejectAct() {
      this.confirmDialog = {
        visible: true,
        action: "reject",
        comment: "",
      };
    },
    async handleDialogConfirm() {
      const isReject = this.confirmDialog.action === "reject";

      if (isReject && !this.confirmDialog.comment.trim()) {
        showToast(this, {
          text: this.$t("acts.rejectCommentRequired"),
          status: "warning",
          duration: 3000,
        });
        return;
      }

      const query = {
        actId: this.$route.params.actId,
        childId: this.$route.params.childId,
        comment: isReject ? this.confirmDialog.comment : undefined,
      };

      const apiFn = isReject ? fetchRejectAct : fetchSubmitAct;
      const successKey = isReject
        ? "toastText.actReject"
        : "toastText.actSuccessSubmit";

      this.isSubmitLoading = true;
      try {
        await apiFn(query);
        showToast(this, {
          text: this.$t(successKey),
          status: "success",
          duration: 3000,
        });
        this.confirmDialog.visible = false;
        this.$router.go(0);
      } catch (error) {
        showToast(this, {
          text: `${this.$t("toastText.errorData")}: ${
            error.response.data.message
          }`,
          status: "error",
          duration: 3000,
        });
      } finally {
        this.isSubmitLoading = false;
      }
    },
    async handleAct({ apiFn, successMessageKey }) {
      try {
        const query = {
          actId: this.$route.params.actId,
          childId: this.$route.params.childId,
        };
        this.isSubmitLoading = true;
        await apiFn(query);
        showToast(this, {
          text: this.$t(successMessageKey),
          status: "success",
          duration: 3000,
        });
      } catch (error) {
        showToast(this, {
          text: `${this.$t("toastText.errorData")}: ${error}`,
          status: "error",
          duration: 3000,
        });
      } finally {
        this.isSubmitLoading = false;
      }
    },
    async initializePageData(actVal) {
      if (this.isLoading) return;

      this.isLoading = true;

      const query = {
        actId: actVal !== "" ? this.$route.params.actId : actVal,
        childId: this.$route.params.childId,
      };
      try {
        await Promise.all([
          this.fetchActInfo(query),
          this.fetchActs(query.childId),
        ]);
      } catch (error) {
        showToast(this, {
          text: `${this.$t(`toastText.errorData`)}: ${error}`,
          status: "error",
          duration: 3000,
        });
      } finally {
        this.isLoading = false;
      }
    },
    selectAct(act) {
      const newActId = act.actNumber;
      const currentActId = this.$route.params.actId;
      if (String(newActId) === String(currentActId)) return;
      this.$router.push({
        name: "Acts",
        params: {
          actId: newActId,
          childId: this.$route.params.childId,
        },
      });

      this.initializePageData(newActId);
    },
  },
};
</script>

<style scoped>
.v-data-table > .v-data-table__wrapper > table > tbody > tr > td,
.v-data-table > .v-data-table__wrapper > table > thead > tr > td,
.v-data-table > .v-data-table__wrapper > table > tfoot > tr > td {
  height: 60px;
}
.acts-v-chip {
  font-weight: bold;
  cursor: pointer;
}
.acts-wrapper,
h2,
h3 {
  font-family: "Montserrat Alternates", sans-serif;
}
.acts-inner-wrapper {
  display: flex;
  justify-content: space-between;
  gap: 40px;
  font-family: "Montserrat", sans-serif;
}

.acts-detail {
  flex: 2;
  min-width: 300px;
}

.acts-list {
  flex: 1;
}
.acts-v-list {
  padding: 0;
}
.section-title {
  font-size: 20px;
  font-weight: bold;
  margin-bottom: 8px;
}

.section-subtitle {
  font-size: 18px;
  font-weight: bold;
  margin-bottom: 16px;
}

.info-card {
  border-radius: 8px;
  background: white;
}

.file-link {
  color: #007bff;
  text-decoration: none;
}

.button-group {
  display: flex;
  gap: 16px;
  margin-top: 16px;
}

.btn.btn {
  position: relative;
  font-size: 18px;
  font-weight: bold;
  color: white;
  padding: 0 !important;
  border-radius: 4px;
  transition: all 0.2s ease-in-out;
  height: 60px;
  min-width: 280px;
  text-transform: none;
  font-family: "Montserrat", sans-serif;
}

.btn.accept-btn {
  background: #00b819;
}

.btn .btn-icon {
  background: #009914;
  padding: 12px;
  border-radius: 4px;
  position: absolute;
  right: 0;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.icon {
  width: 20px;
  height: 20px;
}

.btn.reject-btn {
  background: #aaaaaa;
  color: white;
}

.acts-list-card {
  margin-top: 16px;
  box-shadow: none !important;
}

.acts-item {
  box-shadow: 0px 0px 1px -2px rgba(0, 0, 0, 0.2),
    0px 0px 2px 0px rgba(0, 0, 0, 0.14), 0px 1px 5px 0px rgba(0, 0, 0, 0.12);
  border-radius: 8px;
  margin-bottom: 10px;
}

.view-all {
  text-align: center;
  margin-top: 10px;
}

.view-all-link {
  color: blue;
  font-size: 14px;
  text-decoration: underline;
  font-weight: bold;
}

.diary-section {
  margin-top: 24px;
}

.diary-card {
  border-radius: 8px;
  padding: 16px;
}

.diary-grid {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 1fr;
  gap: 1px;
  background-color: #f0f0f0;
  border: 1px solid #f0f0f0;
}

.diary-header {
  display: contents;
}

.diary-row {
  display: contents;
}

.diary-cell {
  background: white;
  padding: 12px;
  display: flex;
  align-items: center;
}

.diary-header .diary-cell {
  font-weight: bold;
  background: #f5f5f5;
}

.even-row .diary-cell {
  background: #fafafa;
}

@media (max-width: 1024px) {
  .acts-inner-wrapper {
    flex-direction: column;
  }

  .acts-detail,
  .acts-list {
    width: 100%;
  }

  .button-group {
    flex-direction: column;
    gap: 10px;
  }
}

@media (max-width: 600px) {
  .acts-wrapper {
    padding: 12px;
  }

  .section-title {
    font-size: 18px;
  }

  .section-subtitle {
    font-size: 16px;
  }

  .info-card {
    padding: 12px;
  }

  .button-group {
    flex-direction: column;
  }

  .accept-btn,
  .reject-btn {
    width: 100%;
  }
}

@media (max-width: 480px) {
  .section-title {
    text-align: center;
  }

  .section-subtitle {
    text-align: center;
  }
}

</style>
